import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { conversationApi, sellerMessageApi } from './api';

/* ── Query key factories ── */

export const conversationKeys = {
  all: () => ['conversations'],
  detail: (id: any) => ['conversations', id],
};

export const sellerMessageKeys = {
  list: (page: any) => ['seller-messages', page],
};

/* ── Conversation hooks ── */

/**
 * List all conversations. Polls every 10 s by default.
 */
export function useConversations(options = {}) {
  return useQuery({
    queryKey: conversationKeys.all(),
    queryFn: () => conversationApi.list(),
    refetchInterval: 300_000,
    ...options,
  });
}

/**
 * Get a single conversation + messages. Polls every 5 s when enabled.
 */
export function useConversation(conversationId: any, options = {}) {
  return useQuery({
    queryKey: conversationKeys.detail(conversationId),
    queryFn: () => conversationApi.get(conversationId),
    enabled: !!conversationId,
    refetchInterval: 300_000,
    ...options,
  });
}

/**
 * Send a message in a conversation.
 * Optimistically adds the message to the cache.
 */
export function useSendMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, content }: any) => conversationApi.send(conversationId, content),
    onSuccess: (_data: any, { conversationId }: any) => {
      qc.invalidateQueries({ queryKey: conversationKeys.detail(conversationId) });
      qc.invalidateQueries({ queryKey: conversationKeys.all() });
    },
  });
}

/** Start or get admin support conversation. */
export function useContactSupport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => conversationApi.contactSupport(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: conversationKeys.all() });
    },
  });
}

/** Start a new conversation (e.g. from annonce detail page). */
export function useStartConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => conversationApi.start(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: conversationKeys.all() });
    },
  });
}

/* ── Seller messages hooks ── */

/**
 * List paginated seller notification messages.
 */
export function useSellerMessages(page = 1, limit = 20, options = {}) {
  return useQuery({
    queryKey: sellerMessageKeys.list(page),
    queryFn: () => sellerMessageApi.list(page, limit),
    ...options,
  });
}

/**
 * Mark a seller message as read.
 */
export function useMarkSellerMessageRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: any) => sellerMessageApi.markAsRead(messageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['seller-messages'] });
    },
  });
}
